import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { PackageData } from '@/pages/CreatePackage';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useVehicleTypes } from '@/hooks/useTasks';
import { Combobox } from '@/components/ui/combobox';

type Transport = PackageData['transport'][0];

interface AddTransportFormProps {
  onAddTransport: (transport: Transport) => void;
}

const initialTransportState: Omit<Transport, 'price'> & { price: string } = {
  type: 'airport_transfer',
  vehicle: '',
  from: '',
  to: '',
  date: '',
  price: ''
};

export const AddTransportForm = ({ onAddTransport }: AddTransportFormProps) => {
  const [transport, setTransport] = useState(initialTransportState);
  const { vehicleTypes, isLoading: isLoadingVehicleTypes } = useVehicleTypes();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTransport(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: 'type' | 'vehicle') => (value: string) => {
    setTransport(prev => ({ ...prev, [name]: value as any }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!transport.vehicle || !transport.from || !transport.to || !transport.date || !transport.price) {
        toast.error("الرجاء تعبئة جميع الحقول المطلوبة.");
        return;
    }
    onAddTransport({
        ...transport,
        type: transport.type as 'airport_transfer' | 'city_transport' | 'tour_transport',
        price: parseFloat(transport.price)
    });
    setTransport(initialTransportState);
  };

  return (
    <Card className="bg-gray-50">
      <CardHeader>
        <CardTitle className="text-lg">إضافة مواصلات جديدة</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="type">نوع المواصلات</Label>
              <Select name="type" value={transport.type} onValueChange={handleSelectChange('type')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="airport_transfer">استقبال وتوديع</SelectItem>
                  <SelectItem value="city_transport">مواصلات بالمدينة</SelectItem>
                  <SelectItem value="tour_transport">مواصلات جولة سياحية</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="vehicle">نوع السيارة</Label>
              <Combobox
                options={vehicleTypes ? vehicleTypes.map(v => ({ label: v.name_ar, value: v.name_ar })) : []}
                value={transport.vehicle}
                onChange={handleSelectChange('vehicle')}
                placeholder="اختر نوع السيارة"
                searchPlaceholder="ابحث عن نوع السيارة..."
                emptyText="لم يتم العثور على سيارة."
                disabled={isLoadingVehicleTypes}
              />
            </div>
            <div>
              <Label htmlFor="from">من</Label>
              <Input id="from" name="from" value={transport.from} onChange={handleChange} placeholder="مثال: مطار الملك خالد" required />
            </div>
            <div>
              <Label htmlFor="to">إلى</Label>
              <Input id="to" name="to" value={transport.to} onChange={handleChange} placeholder="مثال: فندق حياة ريجنسي" required />
            </div>
            <div>
              <Label htmlFor="date">التاريخ</Label>
              <Input id="date" name="date" type="date" value={transport.date} onChange={handleChange} required />
            </div>
            <div>
              <Label htmlFor="price">السعر (SAR)</Label>
              <Input id="price" name="price" type="number" min="0" value={transport.price} onChange={handleChange} placeholder="0.00" required />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button type="submit">
                <Plus className="w-4 h-4 ml-2" />
                إضافة مواصلات
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
